<template>
  <div class="event-planning">
    <div class="mt5">
      <span class="legend" style="vertical-align:top;">{{ $t("message.REPEATABILITY") + $t("message.TWO_DOTS") }}</span>
      <RadioGroup
        class="inline-radio"
        :items="repeatChoices"
        :value="planning.repeatType"
        :disabled="disabled"
        @input="onInputRepeatType"
      />
    </div>
    <div v-if="intRepeatType === REPEAT_TYPE.daily" class="mt10">
      <div v-for="(day, dayIndex) in dailyDayOptions" class="event-planning__day-option v-centerer">
        <Checkbox
          v-model="planning.repeatData[day.id].on"
          :title="day.title"
          :disabled="disabled"
          class="event-planning__day-option__title"
          @input="$emit('input', planning)"
        />
        <VueTimepicker
          :ref="`dayTime${day.id}`"
          :key="tpKeys[`keyDay${day.id}`]"
          :data-key-index="`Day${day.id}`"
          v-click-outside="rmkTimepicker"
          format="HH:mm"
          :value="pickerValue(day.id)"
          :disabled="disabled || (dayIndex > 0 && applyTimeAllDays)"
          @input="disabled || (dayIndex > 0 && applyTimeAllDays) ? '' : setPickerValue($event, day.id)"
        />
        <Checkbox
          v-if="dayIndex === 0"
          v-model="applyTimeAllDays"
          class="ml10em"
          :title="$t('message.APPLY_TIME_PLANNING')"
          @input="tickApplyAllDays(day.id)"
        />
      </div>
    </div>
    <div v-if="intRepeatType === REPEAT_TYPE.weekly" class="mt10">
      <EditableLabelRadio
        v-model="planning.repeatData.day"
        class="mb10"
        label-key="title"
        style="cursor: pointer;"
        :legend="$t('message.DAY') + $t('message.TWO_DOTS')"
        :items="everyDayData"
        :disabled="disabled"
        @input="$emit('input', planning)"
      />
      <span class="legend">{{ $t("message.TIME") + $t("message.TWO_DOTS") }}</span>
      <VueTimepicker
        ref="weekTime"
        :key="tpKeys.keyWeek"
        data-key-index="Week"
        v-click-outside="rmkTimepicker"
        :value="pickerValue()"
        :disabled="disabled"
        @input="setPickerValue"
      />
    </div>
    <div v-if="intRepeatType === REPEAT_TYPE.monthly" class="mt10">
      <div>
        <span class="legend">{{ $t("message.DAY_OF_MONTH") + $t("message.TWO_DOTS") }}</span>
        <input
          v-model="planning.repeatData.day"
          class="settings-input"
          type="number"
          min="1"
          max="31"
          @input="$emit('input', planning)"
        >
      </div>
      <div class="mt10">
        <span class="legend">{{ $t("message.TIME") + $t("message.TWO_DOTS") }}</span>
        <VueTimepicker
          ref="monthTime"
          :key="tpKeys.keyMonth"
          data-key-index="Month"
          v-click-outside="rmkTimepicker"
          :value="pickerValue()"
          :disabled="disabled"
          @input="setPickerValue"
        />
      </div>
    </div>
    <div v-if="intRepeatType === REPEAT_TYPE.once" class="mt10">
      <div>
        <span class="legend">{{ $t("message.DAY") + $t("message.TWO_DOTS") }}</span>
        <VisualCalendar
          v-model="planning.repeatData.day"
          class="ml10 inline"
          :disabled="disabled"
          @input="$emit('input', planning)"
        />
      </div>
      <div class="mt10 mb10">
        <span class="legend">{{ $t("message.TIME") + $t("message.TWO_DOTS") }}</span>
        <VueTimepicker
          ref="oneTime"
          :key="tpKeys.keyOnce"
          data-key-index="Once"
          v-click-outside="rmkTimepicker"
          :value="pickerValue()"
          :disabled="disabled"
          @input="setPickerValue"
        />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Component used to edit the details of a repeatable event, such as meetings or field tours.
 * Features 4 types of planning, which are daily, weekly, monthly, and once (see REPEAT_TYPE)
 * And each type has its own planning options :
 * - daily: set a time for each week day
 * - weekly/monthly/once: set the day and time
 *
 * @vue-prop {Object} value - The planning object which should hold a 'repeatType' and a 'repeatData' prop.
 *                               repeatData prop is formatted as following :
 *                               - daily: Array of times : [{ on: true, time: '08:00' }, {}] (one obj per working day)
 *                               - weekly/monthly/once: Object : { day: '2 or 23 or 2020-06-07', time: '08:00' }
 * @vue-prop {Boolean} disabled - Whther the edition should be disabled or not
 *
 * @vue-event {Object} input - Triggered on any edit
 */

import { cloneDeep, isEqual } from 'lodash';
// Components
import VueTimepicker from 'vue2-timepicker';
import Checkbox from '@/components/checkbox.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import Icon from '@/components/icon.vue';
import RadioGroup from '@/components/radio-group.vue';
import VisualCalendar from '@/components/visual-calendar.vue';
// Utils
import { DEFAULT_REPEAT_DATA, REPEAT_TYPE } from '@/utils/meeting-utils';
import { copyMedium } from '@/assets/icons';

export default {

  name: 'EventPlanning',

  components: {
    Checkbox,
    EditableLabelRadio,
    Icon,
    RadioGroup,
    VisualCalendar,
    VueTimepicker,
  },

  props: {
    value: {
      type: Object,
      required: true,
    },
    disabled: Boolean,
  },

  data() {
    return {
      copyMedium,
      REPEAT_TYPE,
      repeatData: DEFAULT_REPEAT_DATA,
      repeatChoices: [
        { id: REPEAT_TYPE.daily, label: this.$t('message.EVERY_DAY') },
        { id: REPEAT_TYPE.weekly, label: this.$t('message.EVERY_WEEK') },
        { id: REPEAT_TYPE.monthly, label: this.$t('message.EVERY_MONTH') },
        { id: REPEAT_TYPE.once, label: this.$t('message.ONCE') },
      ],
      everyDayData: [
        { id: 0, title: this.$t('message.MONDAY') },
        { id: 1, title: this.$t('message.TUESDAY') },
        { id: 2, title: this.$t('message.WEDNESDAY') },
        { id: 3, title: this.$t('message.THURSDAY') },
        { id: 4, title: this.$t('message.FRIDAY') },
        { id: 5, title: this.$t('message.SATURDAY') },
        { id: 6, title: this.$t('message.SUNDAY') },
      ],
      tpKeys: {
        keyDay0: 1,
        keyDay1: 1,
        keyDay2: 1,
        keyDay3: 1,
        keyDay4: 1,
        keyDay5: 1,
        keyDay6: 1,
        keyWeek: 1,
        keyMonth: 1,
        keyOnce: 1,
      },
      planning: {},
      applyTimeAllDays: false,
    };
  },

  computed: {
    workingDays() {
      return this.currentTeam.workingDays || [true, true, true, true, true, true, true];
    },
    intRepeatType() {
      return Number(this.planning.repeatType);
    },
    /** Array: available days in the optioons of daily meetings */
    dailyDayOptions() {
      return this.everyDayData.filter((d, dayIndex) => this.workingDays[dayIndex]);
    },
  },

  watch: {
    value: {
      deep: true,
      handler() {
        this.planning = cloneDeep(this.value);
      },
    },
  },

  created() {
    this.planning = cloneDeep(this.value);
    if (this.planning.repeatData == null) {
      this.planning.repeatData = this.repeatData[this.intRepeatType];
    }
  },

  methods: {
    rmkTimepicker(evt, el) {
      if (el && el.dataset.keyIndex !== undefined) {
        if (this.tpKeys[`key${el.dataset.keyIndex}`]) {
          this.tpKeys[`key${el.dataset.keyIndex}`]++;
        }
      }
    },
    /** Notify parent that content changed */
    onInputRepeatType(value) {
      this.planning.repeatType = value;
      this.planning.repeatData = this.repeatData[value];
      this.$emit('input', this.planning);
    },
    /** time value to match timepicker format */
    pickerValue(dayIndex) {
      const time = typeof dayIndex === 'undefined'
        ? this.planning.repeatData.time.split(':')
        : this.planning.repeatData[dayIndex].time.split(':');
      return { HH: time[0] || '08', mm: time[1] || '00' };
    },
    /** match 'hh:mm' format */
    setPickerValue(val, dayIndex) {
      if (!isNaN(val.HH) && !isNaN(val.mm)) {
        if (typeof dayIndex === 'undefined') {
          if (this.planning.repeatData.time !== `${val.HH}:${val.mm}`) {
            this.planning.repeatData.time = `${val.HH}:${val.mm}`;
            this.$emit('input', this.planning);
          }
        } else {
          if (this.planning.repeatData[dayIndex].time !== `${val.HH}:${val.mm}`) {
            this.planning.repeatData[dayIndex].time = `${val.HH}:${val.mm}`;
            this.$emit('input', this.planning);

            if (this.applyTimeAllDays) {
              this.applyTimeToOtherDays(dayIndex);
            }
          }
        }
      }
    },
    /** Copy the value of the selected day to other days (when in daily mode) */
    applyTimeToOtherDays(dayIndex) {
      const time = this.planning.repeatData[dayIndex].time;
      this.everyDayData.forEach((dayData) => {
        this.planning.repeatData[dayData.id].on = true;
        this.planning.repeatData[dayData.id].time = time;
      });
      this.$emit('input', this.planning);
    },
    /** User checked (or unchecked) the option to aply the same time to every day */
    tickApplyAllDays(dayIndex) {
      if (this.applyTimeAllDays) {
        this.applyTimeToOtherDays(dayIndex);
      }
    },
  },
};
</script>

<style lang="scss">
.event-planning {
  &__day-option {
    &__title {
      min-width: 9em;
    }

    .vue__time-picker {
      width: 6rem;

      input.display-time {
        width: 6rem;
      }
    }
  }
}
</style>
